// ============================================================================
// SETTINGS DOMAIN API CLIENT
// ============================================================================
// Domain-specific API for user settings management
// ============================================================================

import { ApiClient } from '../../core/apiClient';

export interface UserSetting {
  id: number;
  user_id: number;
  setting_key: string;
  setting_value: string;
  setting_type: 'boolean' | 'string' | 'number' | 'json';
  category: string;
  created_at: string;
  updated_at: string;
}

export interface SettingUpdate {
  key: string;
  value: any;
  type: 'boolean' | 'string' | 'number' | 'json';
}

export interface SystemConfig {
  id: number;
  config_key: string;
  config_value: string;
  config_type: 'boolean' | 'string' | 'number' | 'json';
  category: string;
  description: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

class SettingsApi extends ApiClient {
  constructor() {
    super({
      enableLogging: import.meta.env.DEV,
    });
  }

  async getUserSettings(): Promise<UserSetting[]> {
    const response = await this.client.get('/settings');
    return response.data.data || response.data || [];
  }

  async updateUserSettings(settings: SettingUpdate[]): Promise<void> {
    await this.client.patch('/settings', { settings });
  }

  async updateUserSetting(key: string, value: any, type: 'boolean' | 'string' | 'number' | 'json'): Promise<void> {
    await this.client.patch(`/settings/${key}`, { value, type });
  }

  async getUserSetting(key: string): Promise<UserSetting | null> {
    try {
      const response = await this.client.get(`/settings/${key}`);
      return response.data.data || response.data || null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getSystemConfigs(): Promise<SystemConfig[]> {
    const response = await this.client.get('/settings/system/all');
    return response.data.data || response.data || [];
  }

  async updateSystemConfigs(configs: SettingUpdate[]): Promise<void> {
    await this.client.patch('/settings/system/bulk', { configs });
  }

  async updateSystemConfig(key: string, value: any, type: 'boolean' | 'string' | 'number' | 'json'): Promise<void> {
    await this.client.patch(`/settings/system/${key}`, { value, type });
  }
}

export const settingsApi = new SettingsApi();
export default settingsApi;